"use client"

import { useState } from "react"
import Image from "next/image"
import dynamic from "next/dynamic"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { AutoplayVideo } from "@/components/ui/autoplay-video"

import { cn } from "@/lib/utils"
import { howItWorksKeys as k } from "@/i18n/how-it-works-keys"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { isPathAllowedInCountry } from "@/lib/country-gates"
import type { Country } from "@/i18n/config"
import {
  ActionPlanIcon,
  BodyFigureIcon,
  ChecklistIcon,
  LifestyleIcon,
} from "@/components/ui/icons"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { CompareMriPackagesDropdown } from "@/components/ui/compare-mri-packages"
import { TabSwitcher } from "@/components/ui/tab-switcher"
import { MembershipTabIcon } from "@/components/ui/membership-tab-icon"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import type { Testimonial } from "@/lib/cms"
import { FaqSection } from "@/components/ui/faq-section"
import { CtaBanner } from "@/components/ui/cta-banner"
import { homeKeys as hk } from "@/i18n/home-keys"
import { isBookingOpen, isWaitlistOnly } from "@/i18n/config"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { blobMediaUrl } from "@/lib/blob-url"
import {
  BloodCellOrganIcon,
  BonesOrganIcon,
  BrainOrganIcon,
  HeartOrganIcon,
  ImmuneOrganIcon,
  KidneysOrganIcon,
  LiverOrganIcon,
  MetabolismOrganIcon,
  SpineOrganIcon,
  UterusOrganIcon,
} from "@/components/ui/organ-icons"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { HomeV4Baseline } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/home/home-v4-baseline"
import { HomeV4BaselineBackground } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/home/home-v4-baseline-background"
import type { PricingV5Data } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-page-data"

const PhoneWalkthrough = dynamic(() =>
  import("@/components/ui/phone-walkthrough").then((m) => ({
    default: m.PhoneWalkthrough,
  }))
)

const TestimonialsSection = dynamic(() =>
  import("../../_components/testimonials-section").then((m) => ({
    default: m.TestimonialsSection,
  }))
)

// Same membership section as /pricing + the homepage, code-split so it doesn't
// bloat the initial bundle.
//
// `loading` is load-bearing, not cosmetic. This only renders under
// `pricingTab === "membership"`, and that tab is never the initial state (it's
// "mri" or "blood"), so the section mounts on a CLICK — never during SSR.
// `next/dynamic` gives a lazy its own Suspense boundary only when `ssr:false`
// OR `loading` is set (`hasSuspenseBoundary` in next's
// `lazy-dynamic/loadable.js`); without one, mounting it suspends up to
// `[country]/[locale]/layout.tsx`'s `<Suspense fallback={null}>`, which wraps
// the entire page. React then hides every painted child of that boundary
// (`display: none !important` on the navbar, `<main>` and footer) and paints
// the `null` fallback until the chunk arrives — measured on production as the
// whole page vanishing for ~300 ms when you click the Membership tab
// (AHD-9122).
const HomepageMembershipSection = dynamic(
  () =>
    import("../../_components/homepage-membership-section").then(
      (m) => m.HomepageMembershipSection
    ),
  { loading: () => null }
)

/* ───────────────────── Disease icons ───────────────────── */

const DISEASE_ICON_MAP: Record<
  string,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  heartDiseaseRisk: HeartOrganIcon,
  arteriosclerosis: HeartOrganIcon,
  diabetes: MetabolismOrganIcon,
  metabolicSyndrome: MetabolismOrganIcon,
  hemochromatosis: BloodCellOrganIcon,
  spinalDysfunction: SpineOrganIcon,
  nerveCompression: SpineOrganIcon,
  structuralIssues: BonesOrganIcon,
  fattyLiver: LiverOrganIcon,
  liverTumors: LiverOrganIcon,
  prostateCancer: KidneysOrganIcon,
  benignHyperplasia: KidneysOrganIcon,
  endometriosis: UterusOrganIcon,
  dementia: BrainOrganIcon,
  brainAneurysms: BrainOrganIcon,
  silentStrokes: BrainOrganIcon,
  neurodegeneration: BrainOrganIcon,
  immuneDysregulation: ImmuneOrganIcon,
  // Blood-detectable risks (the `bloodOnly` marquee). No thyroid icon exists
  // in the organ set, so the two thyroid entries borrow the metabolism glyph.
  prediabetes: MetabolismOrganIcon,
  insulinResistance: MetabolismOrganIcon,
  hypothyroidism: MetabolismOrganIcon,
  hashimoto: MetabolismOrganIcon,
  dyslipidemia: HeartOrganIcon,
  highCholesterol: HeartOrganIcon,
  liverFibrosis: LiverOrganIcon,
  ironDeficiency: BloodCellOrganIcon,
  anemia: BloodCellOrganIcon,
  vitaminB12Deficiency: BloodCellOrganIcon,
  vitaminDDeficiency: BonesOrganIcon,
  kidneyDisease: KidneysOrganIcon,
}

function DiseaseIcon({
  diseaseKey,
  large = false,
}: {
  diseaseKey: string
  large?: boolean
}) {
  const Icon = DISEASE_ICON_MAP[diseaseKey]
  if (!Icon) return null
  return (
    <Icon
      size={large ? 24 : 20}
      className={cn(
        "flex-shrink-0",
        large ? "text-neutral-600" : "text-neutral-500"
      )}
    />
  )
}

function MarqueeRow({
  items,
  duration,
  reverse = false,
  large = false,
}: {
  items: { label: string; key: string }[]
  duration: number
  reverse?: boolean
  /** The membership-first site: bigger pills with no outline, a bigger
   *  icon and medium text (Eugene, 01.10). */
  large?: boolean
}) {
  const doubled = [...items, ...items]
  return (
    <div className="overflow-hidden py-1.5">
      <div
        className="flex gap-3"
        style={{
          animation: `scroll-marquee-hiw ${duration}s linear infinite${reverse ? " reverse" : ""}`,
          width: "max-content",
        }}
      >
        {doubled.map((item, i) => (
          <span
            key={i}
            className={cn(
              "flex flex-shrink-0 items-center rounded-full bg-white whitespace-nowrap",
              large
                ? "type-body-lg-emphasized gap-2.5 px-5 py-3 text-neutral-800"
                : "gap-2 border border-neutral-100 px-4 py-2.5 text-sm text-neutral-700"
            )}
          >
            <DiseaseIcon diseaseKey={item.key} large={large} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  )
}

/* ───────────────────── Walkthrough section data ───────────────────── */

// `bloodImage` replaces the MRI-screen video on blood-only markets (DE), which
// sell no scan: same square, rounded slot, a blood-draw still instead.
const WALKTHROUGH_SECTIONS = [
  {
    video: blobMediaUrl("/videos/how-it-works-1.mp4"),
    bloodImage: blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
    labelKey: "valueCard1Label" as const,
    titleKey: "valueCard1Title" as const,
    descKey: "valueCard1Desc" as const,
  },
  {
    video: blobMediaUrl("/videos/how-it-works-2.mp4"),
    bloodImage: blobMediaUrl("/images/marketing/psa-step2-blood-draw.jpeg"),
    labelKey: "valueCard2Label" as const,
    titleKey: "valueCard2Title" as const,
    descKey: "valueCard2Desc" as const,
  },
  {
    video: blobMediaUrl("/videos/how-it-works-3.mp4"),
    // Not the lauraphilipps tube still: its organ labels (Heart, Metabolism…)
    // are baked into the image in English.
    bloodImage: blobMediaUrl("/images/steps/blood/02-appointment.jpg"),
    labelKey: "valueCard3Label" as const,
    titleKey: "valueCard3Title" as const,
    descKey: "valueCard3Desc" as const,
  },
]

/* ───────────────────── Pricing section component ───────────────────── */

type PricingTab = "mri" | "blood" | "membership"

function PricingSection({
  t,
  k: keys,
  pricingTab,
  setPricingTab,
  country,
  bloodOnly,
}: {
  t: ReturnType<typeof useTranslations<"howItWorks">>
  k: typeof import("@/i18n/how-it-works-keys").howItWorksKeys
  pricingTab: PricingTab
  setPricingTab: (v: PricingTab) => void
  country?: string
  bloodOnly: boolean
}) {
  const lp = useLocalePath()

  // Drop the membership tab wherever there is no membership page to send
  // people to. Was `country !== "nl"` (AHD-6910), which went stale when AT
  // dropped membership on 2026-08-28: `page:membership` is now CH only (DE
  // left it 2026-09-28, AHD-13290), so NL keeps the behaviour it had and AT
  // stops advertising a product it doesn't sell. Reading the registry rather than listing countries means the next
  // market gets this right for free.
  const showMembership = isPathAllowedInCountry(
    "/membership",
    country as Country
  )

  // Whether membership gets a TAB of its own. `bloodOnly` drops it the same
  // way /pricing does: Germany's pricing page shows the blood check-up and its
  // add-ons and nothing else, so the two pages must advertise the same offer.
  const showMembershipTab = !bloodOnly && showMembership

  // AT is the mirror of `bloodOnly`: Austria sells the three MRI packages and
  // nothing else, so the blood tab has no product behind it. The blood work in
  // Advanced and Pro is a package feature, already listed on the MRI cards.
  // country-gate-ignore: AT MRI-only page shape, not a page gate
  const mriOnly = country === "at"

  // What BloodTrackingComparison is told. It reads
  // `showMembership && !isBookingOpen(country, "membership")` to decide that
  // the one-off blood test IS the whole offer and render it as one wide card;
  // `false` instead puts the market on the narrow curated card meant for
  // callers that supply their own extra rows (AHD-9361). Germany has no
  // /membership page any more (AHD-13290), but its membership booking is not
  // open either, so `true` still yields the wide card and never a membership
  // column. `/pricing` sidesteps the question by rendering BloodTestCard.
  const comparisonShowsMembership = showMembership || bloodOnly

  // The "view all" and closing CTAs point at /pricing, which is gated to
  // ["ch","nl","de"] — on AT that is a hard 404, the same trap the nav menus
  // (#2564) and the "Included in" cards (#2817) fell into.
  const pricingAllowed = isPathAllowedInCountry("/pricing", country as Country)

  // Same 3-tab modality switcher as /pricing (MRI · blood · membership), so the
  // two pages stay visually consistent. Labels come from the howItWorks
  // namespace; the icons match /pricing's tab bar.
  const tabItems = [
    ...(bloodOnly
      ? []
      : [
          {
            value: "mri",
            label: t(keys.pricingTabMri),
            icon: (
              <span className="bg-light-blue-300 flex size-[27px] items-center justify-center rounded-full">
                <PersonPackageIcon size={18} className="text-black" />
              </span>
            ),
          },
        ]),
    ...(mriOnly
      ? []
      : [
          {
            value: "blood",
            label: t(keys.pricingTabBlood),
            image: blobMediaUrl("/images/homepage/service-blood-cells.jpeg"),
          },
        ]),
    ...(showMembershipTab
      ? [
          {
            value: "membership",
            label: t(keys.pricingTabMembership),
            icon: <MembershipTabIcon />,
          },
        ]
      : []),
  ]

  return (
    <section className="py-16 md:py-24">
      {/* Header */}
      <div className="mx-auto max-w-[1440px] px-4 text-center md:px-10">
        <div>
          <h2 className="type-display-md text-balance text-neutral-900">
            {t(keys.pricingHeading)}
          </h2>
          {/* Value props — left-aligned column on mobile (centered as a block via mx-auto w-fit),
              two centered rows on desktop. */}
          <div className="mx-auto mt-6 flex w-fit flex-col items-start gap-[10px] md:w-auto md:flex-row md:flex-wrap md:items-center md:justify-center">
            {[
              keys.pricingProp1,
              keys.pricingProp2,
              keys.pricingProp3,
              keys.pricingProp4,
            ].map((key) => (
              <span key={key} className="flex items-center gap-[10px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  className="flex-shrink-0"
                >
                  <path
                    d="M8.6 14.6L15.65 7.55L14.25 6.15L8.6 11.8L5.75 8.95L4.35 10.35L8.6 14.6ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                    fill="#95A73B"
                  />
                </svg>
                <span className="type-body-md font-medium text-neutral-900">
                  {t(key)}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Tab switcher — shared TabSwitcher, same as /pricing. Dropped when
          there is nothing to switch between: /pricing makes the same call
          ("a one-tab switcher is a label that looks clickable"). */}
      {tabItems.length > 1 && (
        <div className="mt-10 flex justify-center px-4">
          <div className="inline-flex max-w-full">
            <TabSwitcher
              tabs={tabItems}
              activeTab={pricingTab}
              onChange={(v) => setPricingTab(v as PricingTab)}
              // Mobile: drop the icon slot + tighten padding so the three labels
              // fit on a 390px viewport (matches /pricing).
              className="max-md:[&_button]:px-3 max-md:[&_button>.shrink-0]:hidden max-md:[&_button>img]:hidden"
            />
          </div>
        </div>
      )}

      {/* MRI tab content */}
      {!bloodOnly && pricingTab === "mri" && (
        <>
          <div>
            <div className="mx-auto mt-10 max-w-[1440px] px-4 md:px-10">
              <MriPackageCards />
            </div>
          </div>

          <CompareMriPackagesDropdown />
        </>
      )}

      {/* Blood tab content — the membership-vs-one-off comparison, same as
          /pricing and /go/bloodtest (replaces the single blood card). */}
      {!mriOnly && pricingTab === "blood" && (
        <BloodTrackingComparison
          className="mx-auto mt-10 max-w-[960px] px-4 md:px-10"
          trackingLocation="how-it-works-blood"
          showMembership={comparisonShowsMembership}
        />
      )}

      {/* Membership tab content — the same membership section as /pricing */}
      {showMembershipTab && pricingTab === "membership" && (
        <div className="mt-10">
          <HomepageMembershipSection fullBleed />
        </div>
      )}

      {/* View all link — matches the "Health risks we may detect" button style.
          Hidden on the membership tab (the section carries its own CTA), and
          wherever /pricing is gated out — there is nothing to view. The
          MRI Compare wrapper already provides 32px below; blood needs mt-8. */}
      {pricingTab !== "membership" && pricingAllowed && (
        <div>
          <div className={cn("text-center", pricingTab === "blood" && "mt-8")}>
            <Link href={lp("/pricing")}>
              <Button
                variant="text"
                className="group"
                iconRight={
                  <ArrowRightIcon
                    size={20}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                }
              >
                {t(keys.viewAllPackages)}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </section>
  )
}

/* ───────────────────── Page component ───────────────────── */

export default function HowItWorksPage({
  country,
  initialFaqs,
  initialTestimonials = [],
  membershipFirst,
  planData,
}: {
  country: string
  initialFaqs: { question: string; answer: string }[]
  initialTestimonials?: Testimonial[]
  membershipFirst: boolean
  /** The baseline card's popup data; loaded only on the membership-first site. */
  planData?: PricingV5Data
}) {
  // Germany sells the blood check-up and its add-ons, nothing else, and
  // /de/de/pricing has already been reshaped around that (`bloodOnly` there).
  // This page was the last DE surface still leading with a scan: the MRI tab,
  // its package cards and the compare dropdown are dropped outright rather
  // than shown with a "Bald verfügbar" pill, on the same reasoning as the
  // homepage — on a page that explains the offer, a product the visitor can't
  // buy reads as part of it. Membership goes with them so the two DE pages
  // advertise the same thing. The MRI copy left in messages/de-DE.json is
  // unrendered, so this comes back by deleting the flag once DE_EXPANSION
  // opens MRI booking in Germany.
  // country-gate-ignore: DE checkup-phase page shape, not a page gate
  const bloodOnly = country === "de"
  // Land on the modality the market sells. MRI where it is bookable, or where
  // it is the whole catalogue (AT — its MRI is waitlist-only, so the old
  // "bookable" rule preselected a blood tab Austria no longer has); blood
  // everywhere else. AHD-6370 / AHD-7869.
  const [pricingTab, setPricingTab] = useState<PricingTab>(
    (isBookingOpen(country, "mri") || country === "at") && !bloodOnly
      ? "mri"
      : "blood"
  )
  const t = useTranslations("howItWorks")
  const ht = useTranslations("home")
  const pt = useTranslations("pricingV5")
  const lp = useLocalePath()
  // See PricingSection: /pricing is gated to ["ch","nl","de"], so on AT the
  // closing CTA would link into a hard 404.
  const pricingAllowed = isPathAllowedInCountry("/pricing", country as Country)
  const { openWaitlist } = useWaitlist()
  const waitlistOnly = isWaitlistOnly(country)

  // Eleven of the eighteen default risks (endometriosis, liver tumours, nerve
  // compression, brain aneurysms, silent strokes, dementia, neurodegeneration,
  // benign hyperplasia, prostate cancer, spinal dysfunction, structural
  // issues) are findings only the scan produces, so on a blood-only market
  // the marquee would advertise detections the check-up cannot make. MS and
  // Alzheimer's are not listed at all (AHD-13092): the scan can show
  // white-matter lesions and atrophy but cannot diagnose either. The
  // replacements are all conditions /what-we-detect already files under the
  // blood check-up.
  // Any market where MRI can't be booked yet (NL at checkup) gets the same
  // list: the scan-only findings aren't something it can detect today.
  const bloodRisksOnly = bloodOnly || !isBookingOpen(country, "mri")
  const HEALTH_RISKS_ROW_1 = bloodRisksOnly
    ? [
        { label: t(k.heartDiseaseRisk), key: "heartDiseaseRisk" },
        { label: t(k.dyslipidemia), key: "dyslipidemia" },
        { label: t(k.highCholesterol), key: "highCholesterol" },
        { label: t(k.diabetes), key: "diabetes" },
        { label: t(k.prediabetes), key: "prediabetes" },
        { label: t(k.insulinResistance), key: "insulinResistance" },
      ]
    : [
        { label: t(k.heartDiseaseRisk), key: "heartDiseaseRisk" },
        { label: t(k.diabetes), key: "diabetes" },
        { label: t(k.fattyLiver), key: "fattyLiver" },
        { label: t(k.spinalDysfunction), key: "spinalDysfunction" },
        { label: t(k.endometriosis), key: "endometriosis" },
        { label: t(k.liverTumors), key: "liverTumors" },
      ]
  const HEALTH_RISKS_ROW_2 = bloodRisksOnly
    ? [
        { label: t(k.metabolicSyndrome), key: "metabolicSyndrome" },
        { label: t(k.fattyLiver), key: "fattyLiver" },
        { label: t(k.liverFibrosis), key: "liverFibrosis" },
        { label: t(k.hypothyroidism), key: "hypothyroidism" },
        { label: t(k.hashimoto), key: "hashimoto" },
        { label: t(k.kidneyDisease), key: "kidneyDisease" },
      ]
    : [
        { label: t(k.brainAneurysms), key: "brainAneurysms" },
        { label: t(k.metabolicSyndrome), key: "metabolicSyndrome" },
        { label: t(k.silentStrokes), key: "silentStrokes" },
        { label: t(k.immuneDysregulation), key: "immuneDysregulation" },
        { label: t(k.benignHyperplasia), key: "benignHyperplasia" },
        { label: t(k.dementia), key: "dementia" },
      ]
  const HEALTH_RISKS_ROW_3 = bloodRisksOnly
    ? [
        { label: t(k.ironDeficiency), key: "ironDeficiency" },
        { label: t(k.anemia), key: "anemia" },
        { label: t(k.vitaminB12Deficiency), key: "vitaminB12Deficiency" },
        { label: t(k.vitaminDDeficiency), key: "vitaminDDeficiency" },
        { label: t(k.hemochromatosis), key: "hemochromatosis" },
        { label: t(k.immuneDysregulation), key: "immuneDysregulation" },
      ]
    : [
        { label: t(k.hemochromatosis), key: "hemochromatosis" },
        { label: t(k.neurodegeneration), key: "neurodegeneration" },
        { label: t(k.nerveCompression), key: "nerveCompression" },
        { label: t(k.prostateCancer), key: "prostateCancer" },
        { label: t(k.arteriosclerosis), key: "arteriosclerosis" },
        { label: t(k.structuralIssues), key: "structuralIssues" },
      ]

  const TRUST_CARDS = [
    { title: t(k.trustCard1Title), description: t(k.trustCard1Desc) },
    { title: t(k.trustCard2Title), description: t(k.trustCard2Desc) },
    { title: t(k.trustCard3Title), description: t(k.trustCard3Desc) },
    { title: t(k.trustCard4Title), description: t(k.trustCard4Desc) },
  ]

  /* Trust card icons — design-system icons matched per content theme */
  const TRUST_ICONS = [
    /* "A broad view of your body" → body figure */
    <BodyFigureIcon key="body" size={32} className="text-aubergine-800" />,
    /* "Tests that work together" → checklist */
    <ChecklistIcon key="checklist" size={32} className="text-aubergine-800" />,
    /* "Pain-free and without radiation" → lifestyle (holistic, gentle) */
    <LifestyleIcon key="lifestyle" size={32} className="text-aubergine-800" />,
    /* "Personalized insights" → target + sparkle = personalized plan */
    <ActionPlanIcon key="action" size={32} className="text-aubergine-800" />,
  ]

  return (
    <>
      {/* ─── 1. Hero with image collage ─── */}
      <section className="pt-12 pb-4 text-center md:pt-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{t(k.heroLabel)}</p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
            {t(k.heroHeading)}
          </h1>
        </div>

        {/* Image collage — 2 images like Framer. The membership-first site
            has a fresh pair, at quality 90, and 40px under the heading. */}
        <div
          className={`mx-auto ${membershipFirst ? "mt-10" : "mt-8"} grid max-w-[1440px] grid-cols-2 gap-4 px-4 md:px-10`}
        >
          <div className="relative aspect-[3/4] overflow-hidden rounded-[16px] md:aspect-[6/5]">
            <Image
              src={blobMediaUrl(
                membershipFirst
                  ? "/images/how-it-works/hero-portrait-fresh-20260930-v1.png"
                  : "/images/how-it-works/hero-portrait.jpeg"
              )}
              alt=""
              fill
              quality={membershipFirst ? 90 : undefined}
              sizes="(min-width: 1024px) 50vw, 50vw"
              className="object-cover"
              priority
            />
          </div>
          <div className="relative aspect-[3/4] overflow-hidden rounded-[16px] md:aspect-[6/5]">
            <Image
              src={blobMediaUrl(
                membershipFirst
                  ? "/images/how-it-works/hero-app-fresh-report-20260930-v2.png"
                  : "/images/how-it-works/hero-app.png"
              )}
              alt="Ahead Health app"
              fill
              quality={membershipFirst ? 90 : undefined}
              sizes="(min-width: 1024px) 50vw, 50vw"
              className="object-cover"
              priority
            />
          </div>
        </div>

        {/* Large subtitle text below images */}
        <div>
          <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-10 md:py-16">
            <p
              className={`${membershipFirst ? "type-headline-lg-emphasized" : "type-display-sm"} text-left text-balance text-neutral-900 md:max-w-[50%]`}
            >
              {t(k.heroSubtitle)}
            </p>
          </div>
        </div>
      </section>

      {/* ─── 2. Walkthrough sections — alternating left/right with timeline ─── */}
      <section className="py-8">
        {WALKTHROUGH_SECTIONS.map((section, index) => {
          const isReversed = index % 2 === 1
          return (
            <div key={index}>
              <div>
                <div
                  className={cn(
                    "mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-8 px-4 py-12 md:grid-cols-[1.5fr_1fr] md:gap-16 md:px-10 md:py-16",
                    isReversed && "md:[direction:rtl]"
                  )}
                >
                  {/* Text side */}
                  <div
                    className={cn(
                      "md:max-w-sm",
                      isReversed
                        ? "md:mx-auto md:[direction:ltr]"
                        : "md:mr-auto md:ml-16"
                    )}
                  >
                    <p className="type-label-lg mb-3 text-neutral-400">
                      {t(k[section.labelKey])}
                    </p>
                    <h2 className="type-display-sm text-balance text-neutral-900">
                      {t(k[section.titleKey])}
                    </h2>
                    <p className="type-body-lg mt-4 leading-relaxed text-balance text-neutral-600">
                      {t(k[section.descKey])}
                    </p>
                  </div>

                  {/* Media side: MRI-screen video, or a blood-draw still on
                      blood-only markets. Same square slot either way. */}
                  <div className={cn(isReversed && "md:[direction:ltr]")}>
                    {bloodOnly ? (
                      <div className="relative aspect-square w-full overflow-hidden rounded-[16px]">
                        <Image
                          src={section.bloodImage}
                          alt=""
                          fill
                          sizes="(min-width: 768px) 40vw, 100vw"
                          className="object-cover"
                        />
                      </div>
                    ) : (
                      <AutoplayVideo className="aspect-square w-full rounded-[16px] object-cover">
                        <source src={section.video} type="video/mp4" />
                      </AutoplayVideo>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </section>

      {/* ─── 3. Health risks marquee ─── */}
      <style>{`
        @keyframes scroll-marquee-hiw {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-4 text-center md:px-10">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(k.healthRisksHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-md text-balance text-neutral-600">
              {t(k.healthRisksSubtitle)}
            </p>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-[1440px]">
          <div>
            <MarqueeRow
              items={HEALTH_RISKS_ROW_1}
              duration={35}
              large={membershipFirst}
            />
            <MarqueeRow
              items={HEALTH_RISKS_ROW_2}
              duration={40}
              reverse
              large={membershipFirst}
            />
            <MarqueeRow
              items={HEALTH_RISKS_ROW_3}
              duration={30}
              large={membershipFirst}
            />
          </div>
        </div>
        {/* Health risks link */}
        <div>
          <div className="mt-6 text-center">
            <Link href={lp("/what-we-detect")}>
              <Button
                variant="text"
                className="group h-auto max-w-full text-balance whitespace-normal"
                iconRight={
                  <ArrowRightIcon
                    size={20}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                }
              >
                {t(k.healthRisksLink).replace(/\s*›+\s*$/, "")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 4. Phone walkthrough: scroll-linked steps, or on the plan site
          the four phones side by side (Eugene, 09.10) ─── */}
      <PhoneWalkthrough bloodOnly={bloodOnly} row={membershipFirst} />

      {/* ─── 5. Full pricing section ───
          The membership-first site sells one thing, the membership, with the
          MRI as the add-on you can switch on: the homepage's baseline card
          says exactly that, so it replaces the MRI / blood / membership tabs
          and their "view all packages" link. */}
      {membershipFirst && planData ? (
        <HomeV4BaselineBackground>
          <HomeV4Baseline
            section="mx-auto max-w-[1440px] px-4 md:px-10"
            planData={planData}
          />
          {/* Every test and its price, on /pricing (Eugene, 30.09). Pulled
              up into the card section's own bottom padding, so it reads as
              the card's link. */}
          <div className="-mt-8 pb-10 text-center md:-mt-16 md:pb-14">
            <Link href={lp("/pricing")}>
              <Button
                variant="text"
                className="group"
                iconRight={
                  <ArrowRightIcon
                    size={20}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                }
              >
                {pt("allTestsLink")}
              </Button>
            </Link>
          </div>
        </HomeV4BaselineBackground>
      ) : (
        <PricingSection
          t={t}
          k={k}
          pricingTab={pricingTab}
          setPricingTab={setPricingTab}
          country={country}
          bloodOnly={bloodOnly}
        />
      )}

      {/* ─── 6. Trust signals — "Your health is our priority" ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto mb-12 max-w-6xl px-4 text-center md:px-10">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(k.trustSectionHeading)}
            </h2>
          </div>
        </div>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 sm:grid-cols-2 md:px-10 lg:grid-cols-4">
          {TRUST_CARDS.map((card, index) => (
            <div key={index}>
              <div className="flex flex-col items-start text-left">
                {TRUST_ICONS[index]}
                <h3 className="type-headline-sm mt-4 text-balance text-neutral-900 md:max-w-[15ch]">
                  {card.title}
                </h3>
                <p className="type-body-md mt-2 leading-relaxed text-neutral-500">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing section is now in section 5 above */}

      {/* ─── 8. Insurance section ─── */}
      <InsuranceSection trackingLocation="how-it-works-insurance" />

      {/* ─── 9. Testimonials — shared carousel from homepage ─── */}
      <TestimonialsSection
        heading={t(k.testimonialsHeading)}
        description={null}
        cmsTestimonials={initialTestimonials}
      />

      {/* ─── 10. FAQ — shared two-column section, multi-open enabled ─── */}
      <FaqSection
        heading={t(k.faqHeading)}
        items={initialFaqs}
        linkLabel={null}
        allowMultiple
        currentPage="how-it-works"
      />

      {/* ─── 11. Bottom CTA — shared CtaBanner from homepage ─── */}
      <div>
        <CtaBanner
          title={ht(hk.ctaTitle)}
          subtitle={ht(hk.ctaSubtitle)}
          // The membership-first site's closing photo, with the centred
          // overlay (black primary, frosted secondary).
          imageSrc={blobMediaUrl(
            membershipFirst
              ? "/images/marketing/male-cta-sunlight-v1-20261001.webp"
              : "/images/homepage/cta-bottom.jpeg"
          )}
          overlay={membershipFirst ? "center" : undefined}
          primaryCta={
            // Waitlist markets (AT) label this "Auf die Warteliste", so it
            // opens the waitlist modal like the homepage CTA (AHD-13686).
            waitlistOnly
              ? { label: ht(hk.ctaPrimary), onClick: () => openWaitlist() }
              : {
                  label: ht(hk.ctaPrimary),
                  // AT has no /pricing (no EUR price map), so the closing CTA
                  // falls back to the check-up listing rather than a 404.
                  href: pricingAllowed ? lp("/pricing") : lp("/tests"),
                }
          }
          secondaryCta={{ label: ht(hk.ctaSecondary), href: lp("/contact") }}
        />
      </div>
    </>
  )
}
